import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { 
  Code, Trophy, Clock, CheckCircle, XCircle, Palette, Layout, 
  Database, Award, Volume2, VolumeX, Mic, Headphones, BookOpen, 
  Search, Check, ChevronLeft, ChevronRight, ChevronDown, Share2 
} from 'lucide-react';
import { courses } from './data/courses';
import { learningGuides } from './data/learningGuides';

const themes = {
  light: {
    name: 'Modern Light',
    bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
    card: 'bg-white',
    text: 'text-gray-800',
    subText: 'text-gray-600',
    primary: 'bg-gradient-to-r from-blue-600 to-purple-600',
    primaryText: 'text-blue-600',
    accent: 'text-purple-600',
    highlight: 'bg-blue-50',
    border: 'border-blue-200'
  },
  dark: {
    name: 'Dark Mode',
    bg: 'bg-slate-900',
    card: 'bg-slate-800',
    text: 'text-white',
    subText: 'text-slate-400',
    primary: 'bg-gradient-to-r from-indigo-500 to-purple-500',
    primaryText: 'text-indigo-400',
    accent: 'text-purple-400',
    highlight: 'bg-slate-700',
    border: 'border-slate-700'
  },
  nature: {
    name: 'Nature',
    bg: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50',
    card: 'bg-white',
    text: 'text-emerald-900',
    subText: 'text-emerald-700',
    primary: 'bg-gradient-to-r from-emerald-600 to-teal-600',
    primaryText: 'text-emerald-700',
    accent: 'text-teal-600',
    highlight: 'bg-emerald-50',
    border: 'border-emerald-200'
  },
  ocean: {
    name: 'Ocean',
    bg: 'bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50',
    card: 'bg-white/90 backdrop-blur',
    text: 'text-cyan-900',
    subText: 'text-slate-600',
    primary: 'bg-gradient-to-r from-cyan-600 to-blue-600',
    primaryText: 'text-cyan-700',
    accent: 'text-blue-600',
    highlight: 'bg-cyan-50',
    border: 'border-cyan-200'
  }
};

const S4SimpleLogo = ({ size = 48, showText = true, textClassName = "" }) => (
  <div className="flex items-center gap-3 select-none group">
    <div className="relative flex items-center justify-center">
      <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500 rounded-2xl blur-sm opacity-70 group-hover:opacity-100 transition duration-300"></div>
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="relative shadow-xl rounded-2xl transform transition-transform group-hover:scale-105"
      >
        <rect width="64" height="64" rx="16" fill="url(#s4_grad_bg)" />
        <path d="M0 16H64M0 32H64M0 48H64M16 0V64M32 0V64M48 0V64" stroke="white" strokeOpacity="0.06" strokeWidth="1" />
        <path d="M18 24L12 32L18 40" stroke="#A7F3D0" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M46 24L52 32L46 40" stroke="#A7F3D0" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <text
          x="32"
          y="41"
          textAnchor="middle"
          fill="white"
          fontSize="16"
          fontWeight="800"
          fontFamily="system-ui, -apple-system, sans-serif"
          letterSpacing="-1"
        >
          _
        </text>
        <path d="M48 14L49.5 17.5L53 19L49.5 20.5L48 24L46.5 20.5L43 19L46.5 17.5L48 14Z" fill="#FDE047" />
        <defs>
          <linearGradient id="s4_grad_bg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
            <stop stopColor="#4F46E5" />
            <stop offset="0.5" stopColor="#7C3AED" />
            <stop offset="1" stopColor="#059669" />
          </linearGradient>
        </defs>
      </svg>
    </div>

    {showText && (
      <div className="flex flex-col text-left">
        <div className="flex items-center gap-1.5">
          <span className={`font-extrabold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-emerald-600 dark:from-indigo-400 dark:via-purple-300 dark:to-emerald-400 bg-clip-text text-transparent ${textClassName || 'text-3xl'}`}>
            S4simple
          </span>
          <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 border border-emerald-300/40 dark:border-emerald-700/40 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
            .com
          </span>
        </div>
      </div>
    )}
  </div>
);

const SEOUpdater = ({ title, description }) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }
    if (description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = description;
    }
  }, [title, description]);
  return null;
};

const ShareButton = ({ url, label = "Share Link", className = "" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    const fullUrl = url || window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        // Fallback
        fallbackCopyTextToClipboard(fullUrl);
      });
    } else {
      fallbackCopyTextToClipboard(fullUrl);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Fallback copy failed', err);
    }
    document.body.removeChild(textArea);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border cursor-pointer ${copied
          ? 'bg-emerald-600 border-transparent text-white shadow-sm'
          : 'bg-transparent hover:bg-gray-100 dark:hover:bg-slate-800/80 border-gray-300 text-gray-500 dark:text-gray-300'
        } ${className}`}
      title="Copy shareable URL to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 stroke-[3]" /> : <Share2 className="w-3.5 h-3.5" />}
      {copied ? 'Link Copied!' : label}
    </button>
  );
};

const renderText = (text) => {
  if (!text) return null;

  const parts = text.split(/```([\s\S]*?)```/g);

  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return (
        <pre key={index} className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto my-2 text-sm font-mono">
          <code>{part.trim()}</code>
        </pre>
      );
    }

    const inlineParts = part.split(/`([^`]+)`/g);
    return (
      <span key={index}>
        {inlineParts.map((subPart, subIndex) => {
          if (subIndex % 2 === 1) {
            return (
              <code key={subIndex} className="bg-gray-100 text-red-500 px-1 rounded font-mono text-sm border border-gray-200">
                {subPart}
              </code>
            );
          }

          const boldParts = subPart.split(/\*\*(.*?)\*\*/g);
          return (
            <span key={subIndex}>
              {boldParts.map((boldPart, boldIndex) => {
                if (boldIndex % 2 === 1) {
                  return <strong key={boldIndex}>{boldPart}</strong>;
                }
                return boldPart;
              })}
            </span>
          );
        })}
      </span>
    );
  });
};

const speakText = (text) => {
  if (!text) return;
  window.speechSynthesis.cancel();
  const cleanText = text.replace(/[*`#]/g, '');
  const utterance = new SpeechSynthesisUtterance(cleanText);
  utterance.rate = 1.0;
  utterance.pitch = 1.0;
  window.speechSynthesis.speak(utterance);
};

const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'Easy': return 'bg-green-100 text-green-800';
    case 'Medium': return 'bg-yellow-100 text-yellow-800';
    case 'Hard': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const processQuestions = (questions) => {
  return questions.map(q => {
    const optionsWithIndex = q.options.map((opt, i) => ({ value: opt, originalIndex: i }));
    const shuffledOptions = optionsWithIndex.sort(() => 0.5 - Math.random());
    const newCorrectIndex = shuffledOptions.findIndex(item => item.originalIndex === q.correct);

    return {
      ...q,
      options: shuffledOptions.map(o => o.value),
      correct: newCorrectIndex
    };
  });
};

// Top Floating Navbar Controls (Music & Theme)
const HeaderControls = ({ isMusicPlaying, setIsMusicPlaying, currentTheme, setCurrentTheme, theme }) => (
  <div className="absolute top-4 right-4 z-10 flex gap-2">
    <button
      onClick={() => setIsMusicPlaying(!isMusicPlaying)}
      className={`p-3 rounded-full ${theme.card} shadow-lg hover:shadow-xl transition-all cursor-pointer`}
      title={isMusicPlaying ? "Mute Music" : "Play Music"}
    >
      {isMusicPlaying ? (
        <Volume2 className={`w-6 h-6 ${theme.accent}`} />
      ) : (
        <VolumeX className={`w-6 h-6 ${theme.subText}`} />
      )}
    </button>
    <button
      onClick={() => {
        const keys = Object.keys(themes);
        const nextIndex = (keys.indexOf(currentTheme) + 1) % keys.length;
        setCurrentTheme(keys[nextIndex]);
      }}
      className={`p-3 rounded-full ${theme.card} shadow-lg hover:shadow-xl transition-all cursor-pointer`}
      title={`Current Theme: ${theme.name}`}
    >
      <Palette className={`w-6 h-6 ${theme.accent}`} />
    </button>
  </div>
);

// Dashboard Page Component
const DashboardView = ({ theme, isMusicPlaying, setIsMusicPlaying, currentTheme, setCurrentTheme, masteredList }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname.startsWith('/quizzes') ? 'quizzes' : 'guides';

  return (
    <div className={`min-h-screen ${theme.bg} transition-colors duration-500`}>
      <SEOUpdater
        title="S4simple.com - Tech Interview Prep & Certification Platform"
        description="Master Technical Interviews & Certifications with Interactive Practice Quizzes & Senior Developer Q&A Guides"
      />
      <HeaderControls
        isMusicPlaying={isMusicPlaying}
        setIsMusicPlaying={setIsMusicPlaying}
        currentTheme={currentTheme}
        setCurrentTheme={setCurrentTheme}
        theme={theme}
      />

      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center mb-8 pt-6 flex flex-col items-center">
          <div className="mb-4">
            <S4SimpleLogo size={64} textClassName="text-5xl md:text-6xl" />
          </div>
          <h1 className="sr-only">S4simple.com - Tech Interview Prep</h1>
          <p className={`${theme.subText} text-lg md:text-xl font-medium max-w-2xl text-center`}>
            Master Technical Interviews & Certifications with Interactive Practice & Senior Developer Q&A Guides
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-250/20 dark:bg-slate-800/50 p-1.5 rounded-xl flex gap-1 backdrop-blur-sm border border-gray-200/30 dark:border-slate-700/30">
            <button
              onClick={() => navigate('/')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-sm transition-all cursor-pointer ${activeTab === 'guides'
                ? `${theme.primary} text-white shadow-md`
                : `${theme.text} hover:bg-gray-305/50 dark:hover:bg-slate-700/50`
                }`}
            >
              <BookOpen className="w-4 h-4" />
              Q&A Study Guides
            </button>
            <button
              onClick={() => navigate('/quizzes')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-sm transition-all cursor-pointer ${activeTab === 'quizzes'
                ? `${theme.primary} text-white shadow-md`
                : `${theme.text} hover:bg-gray-305/50 dark:hover:bg-slate-700/50`
                }`}
            >
              <Trophy className="w-4 h-4" />
              Practice Quizzes
            </button>
          </div>
        </div>

        {activeTab === 'quizzes' ? (
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {courses.map(course => {
              const Icon = { Code, Layout, Database, Award }[course.icon] || Code;
              return (
                <div
                  key={course.id}
                  onClick={() => navigate(`/quizzes/${course.id}`)}
                  className={`${theme.card} rounded-2xl shadow-xl p-8 cursor-pointer hover:shadow-2xl hover:scale-105 transition-all group border-2 border-transparent hover:${theme.border}`}
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${course.color} flex items-center justify-center mb-6 shadow-lg group-hover:rotate-6 transition-transform`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className={`text-3xl font-bold ${theme.text} mb-3`}>{course.title}</h2>
                  <p className={`${theme.subText} text-lg mb-6`}>{course.description}</p>
                  <div className="flex items-center justify-between">
                    <span className={`px-4 py-2 rounded-full ${course.bg} ${course.accent} font-bold text-sm`}>
                      {course.questions.length} Questions
                    </span>
                    <span className={`font-semibold ${theme.primaryText} flex items-center group-hover:translate-x-2 transition-transform`}>
                      Start Practice →
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {learningGuides.map(guide => {
              const Icon = { Code, Layout, Database, Award }[guide.icon] || Award;
              const masteredCount = guide.questions.filter(q => masteredList[`${guide.id}_${q.id}`]).length;
              const progressPct = guide.questions.length > 0 ? Math.round((masteredCount / guide.questions.length) * 100) : 0;
              const firstQuestionId = guide.questions[0]?.id;

              return (
                <div
                  key={guide.id}
                  onClick={() => navigate(`/guides/${guide.id}${firstQuestionId ? `/${firstQuestionId}` : ''}`)}
                  className={`${theme.card} rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-xl hover:scale-[1.03] transition-all group border border-gray-150 dark:border-slate-800 flex flex-col justify-between`}
                >
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center shadow-md">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-orange-505 font-bold text-xs bg-orange-50 dark:bg-orange-950/20 px-2 py-0.5 rounded">
                        {guide.rating}
                      </span>
                    </div>
                    <h2 className={`text-xl font-bold ${theme.text} mb-1 group-hover:${theme.primaryText} transition-colors`}>{guide.title}</h2>
                    <p className="text-xs text-gray-400 font-bold mb-3">{guide.subtitle}</p>
                    <p className={`${theme.subText} text-sm mb-4 line-clamp-3 leading-relaxed`}>{guide.description}</p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-slate-700/50">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Mastered: {masteredCount}/{guide.questions.length}</span>
                      <span>{progressPct}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-slate-700 h-1.5 rounded-full mb-3">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-450">{guide.questions.length} reading items</span>
                      <span className={`text-sm font-bold ${theme.primaryText} group-hover:translate-x-1 transition-transform`}>
                        Open Guide →
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Learning Guide Viewer Page Component (Routes: /guides/:guideId and /guides/:guideId/:questionId)
const GuideViewer = ({ theme, isMusicPlaying, setIsMusicPlaying, currentTheme, setCurrentTheme, masteredList, toggleMastered }) => {
  const navigate = useNavigate();
  const { guideId, questionId } = useParams();

  const currentGuide = useMemo(() => {
    return learningGuides.find(g => g.id === guideId) || null;
  }, [guideId]);

  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [alwaysShowAnswer, setAlwaysShowAnswer] = useState(true);
  const [showAnswer, setShowAnswer] = useState(true);
  const [isAutoRead, setIsAutoRead] = useState(false);
  const [isQaListOpen, setIsQaListOpen] = useState(false);

  const guideQuestions = useMemo(() => {
    return currentGuide ? currentGuide.questions : [];
  }, [currentGuide]);

  const guideCategories = useMemo(() => {
    return currentGuide
      ? ['all', ...new Set(guideQuestions.map(q => q.category))]
      : [];
  }, [currentGuide, guideQuestions]);

  const filteredQuestions = useMemo(() => {
    if (!currentGuide) return [];
    return guideQuestions.filter(q => {
      const matchesCategory = category === 'all' || q.category === category;
      const matchesSearch = !searchQuery.trim() ||
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [currentGuide, guideQuestions, category, searchQuery]);

  // Determine current active question index based on URL parameter or fallback
  const currentQuestionIndex = useMemo(() => {
    if (!filteredQuestions.length) return 0;
    if (questionId) {
      const foundIdx = filteredQuestions.findIndex(q => q.id === questionId);
      if (foundIdx !== -1) return foundIdx;
    }
    return 0;
  }, [filteredQuestions, questionId]);

  const currentQ = filteredQuestions[currentQuestionIndex];
  const activeMastered = (currentGuide && currentQ) ? masteredList[`${currentGuide.id}_${currentQ.id}`] : false;

  // Navigate to target question by ID
  const selectQuestionById = (targetQId) => {
    navigate(`/guides/${guideId}/${targetQId}`);
    setShowAnswer(alwaysShowAnswer);
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!currentGuide || !filteredQuestions.length) return;
      if (e.key === 'ArrowRight') {
        if (currentQuestionIndex < filteredQuestions.length - 1) {
          const nextQ = filteredQuestions[currentQuestionIndex + 1];
          selectQuestionById(nextQ.id);
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentQuestionIndex > 0) {
          const prevQ = filteredQuestions[currentQuestionIndex - 1];
          selectQuestionById(prevQ.id);
        }
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        if (document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
          e.preventDefault();
          setShowAnswer(prev => !prev);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentGuide, filteredQuestions, currentQuestionIndex, alwaysShowAnswer]);

  // Auto-Read Speech Synthesis
  useEffect(() => {
    if (isAutoRead && currentQ && !showAnswer) {
      const timeout = setTimeout(() => {
        speakText(currentQ.question);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      window.speechSynthesis.cancel();
    }
  }, [currentQ, isAutoRead, showAnswer]);

  // Save Progress locally
  useEffect(() => {
    if (currentGuide && currentQ) {
      localStorage.setItem('guide_progress_' + currentGuide.id, currentQuestionIndex);
    }
  }, [currentGuide, currentQ, currentQuestionIndex]);

  if (!currentGuide) {
    return <Navigate to="/" replace />;
  }

  const currentShareUrl = window.location.origin + `/guides/${currentGuide.id}${currentQ ? `/${currentQ.id}` : ''}`;
  const seoTitle = currentQ
    ? `${currentQ.question.substring(0, 60)}... - ${currentGuide.title} | S4simple`
    : `${currentGuide.title} - Tech Interview Q&A | S4simple`;

  return (
    <div className={`min-h-screen ${theme.bg} transition-colors duration-500`}>
      <SEOUpdater
        title={seoTitle}
        description={currentGuide.subtitle || currentGuide.description}
      />

      {/* Header toolbar */}
      <div className={`border-b border-gray-200/50 dark:border-slate-800/80 ${theme.card} sticky top-0 z-20 backdrop-blur-md bg-white/70 dark:bg-slate-900/70`}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className={`flex items-center gap-1.5 text-sm font-bold ${theme.subText} hover:${theme.primaryText} transition-colors cursor-pointer shrink-0`}
            >
              ← Back to Dashboard
            </button>
            <div className="h-4 w-[1px] bg-gray-300 dark:bg-slate-700"></div>
            <span className={`text-sm font-extrabold ${theme.text} truncate`}>{currentGuide.title}</span>
            <span className="text-xs text-orange-500 font-bold bg-orange-100/50 dark:bg-orange-950/20 px-2 py-0.5 rounded shrink-0">{currentGuide.rating}</span>
          </div>

          <ShareButton url={currentShareUrl} label="Share Guide" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar filtering & selection */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          <div className={`${theme.card} p-4 rounded-2xl shadow-md border border-gray-200/50 dark:border-slate-800`}>
            <h3 className={`font-bold ${theme.text} mb-3 flex items-center gap-2 text-sm`}>
              <BookOpen className="w-4.5 h-4.5 text-indigo-500" /> Options & Filter
            </h3>

            {/* Search input */}
            <div className="relative mb-3 flex items-center">
              <Search className="w-4 h-4 text-gray-400 absolute left-3" />
              <input
                type="text"
                placeholder="Search Q&A..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-100 dark:bg-slate-850 rounded-lg py-2 pl-9 pr-4 text-xs focus:outline-none border-2 border-transparent focus:border-indigo-500 text-gray-800 dark:text-gray-150"
              />
            </div>

            {/* Category dropdown */}
            <div className="mb-4">
              <label className="block text-[10px] font-bold text-gray-500 mb-1">CATEGORY</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-gray-100 dark:bg-slate-850 rounded-lg p-2 text-xs focus:outline-none border-2 border-transparent focus:border-indigo-500 text-gray-800 dark:text-gray-200"
              >
                {guideCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Always reveal toggle */}
            <label className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-slate-800/50 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={alwaysShowAnswer}
                onChange={(e) => {
                  setAlwaysShowAnswer(e.target.checked);
                  if (e.target.checked) setShowAnswer(true);
                }}
                className="rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className={theme.text}>Auto-reveal answers</span>
            </label>
          </div>

          {/* List of filtered questions */}
          <div className={`${theme.card} p-4 rounded-2xl shadow-md border border-gray-200/50 dark:border-slate-800`}>
            <button
              onClick={() => setIsQaListOpen(!isQaListOpen)}
              className="w-full flex items-center justify-between text-left cursor-pointer group focus:outline-none"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
                  Q&A items ({filteredQuestions.length})
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-semibold group-hover:underline">
                <span>{isQaListOpen ? 'Collapse' : 'Expand'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isQaListOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {isQaListOpen && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700/50 max-h-[450px] overflow-y-auto space-y-1 transition-all duration-300">
                {filteredQuestions.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No results found.</p>
                ) : (
                  filteredQuestions.map((q, idx) => {
                    const isSelected = currentQuestionIndex === idx;
                    const isM = masteredList[`${currentGuide.id}_${q.id}`];
                    return (
                      <button
                        key={q.id}
                        onClick={() => selectQuestionById(q.id)}
                        className={`w-full text-left p-2.5 rounded-lg text-xs leading-relaxed transition-all flex items-start gap-2 cursor-pointer ${isSelected
                          ? 'bg-blue-50 dark:bg-slate-700 text-blue-700 dark:text-blue-300 font-bold border border-blue-200/50 dark:border-blue-900/50'
                          : `text-gray-650 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800/30 border border-transparent`
                          }`}
                      >
                        <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${isM ? 'bg-green-500 border-transparent text-white' : 'border-gray-300 dark:border-slate-600'
                          }`}>
                          {isM && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                        </span>
                        <span className="truncate flex-1">{q.question}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Question Display column */}
        <div className="lg:col-span-3">
          {filteredQuestions.length === 0 ? (
            <div className={`${theme.card} p-12 rounded-3xl shadow-xl border border-gray-200/50 dark:border-slate-800 text-center`}>
              <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className={`text-2xl font-bold ${theme.text} mb-2`}>No matching study items</h3>
              <p className={`${theme.subText} mb-6`}>Adjust your search query or select another category.</p>
              <button
                onClick={() => {
                  setCategory('all');
                  setSearchQuery('');
                }}
                className={`${theme.primary} text-white px-6 py-2.5 rounded-xl font-bold hover:shadow transition-all cursor-pointer`}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className={`${theme.card} p-8 rounded-3xl shadow-xl border border-gray-200/50 dark:border-slate-800 relative`}>
                {/* Category, Difficulty & Actions toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6 border-b pb-4 border-gray-150 dark:border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold bg-indigo-50 dark:bg-slate-750 text-indigo-700 dark:text-indigo-300 px-3 py-1 rounded-full uppercase">
                      {currentQ.category}
                    </span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${getDifficultyColor(currentQ.difficulty)}`}>
                      {currentQ.difficulty}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <ShareButton
                      url={currentShareUrl}
                      label="Share Question"
                    />

                    <button
                      onClick={() => toggleMastered(currentGuide.id, currentQ.id)}
                      className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border cursor-pointer ${activeMastered
                        ? 'bg-green-600 border-transparent text-white shadow-sm'
                        : `bg-transparent hover:bg-gray-100 dark:hover:bg-slate-800/80 border-gray-300 text-gray-500 dark:text-gray-300`
                        }`}
                    >
                      <Check className="w-3.5 h-3.5 stroke-[3]" />
                      {activeMastered ? 'Mastered!' : 'Mark as Mastered'}
                    </button>

                    <div className="flex gap-2 border-l pl-2 border-gray-200 dark:border-slate-700">
                      <button
                        onClick={() => speakText(currentQ.question + (showAnswer ? ". Explanation: " + currentQ.answer : ""))}
                        className={`p-2 rounded-full ${theme.highlight} hover:bg-gray-250 dark:hover:bg-slate-700 transition-colors cursor-pointer`}
                        title="Read Q&A Out Loud"
                      >
                        <Headphones className={`w-4 h-4 ${theme.primaryText}`} />
                      </button>
                      <button
                        onClick={() => setIsAutoRead(!isAutoRead)}
                        className={`p-2 rounded-full ${isAutoRead ? theme.primary : theme.highlight} transition-colors cursor-pointer`}
                        title={isAutoRead ? "Auto-Read On" : "Auto-Read Off"}
                      >
                        <Mic className={`w-4 h-4 ${isAutoRead ? 'text-white' : theme.subText}`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="mb-6">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-2">Question {currentQuestionIndex + 1} of {filteredQuestions.length}</span>
                  <h3 className={`text-2xl md:text-3xl font-extrabold ${theme.text} leading-snug`}>
                    {renderText(currentQ.question)}
                  </h3>
                </div>

                {/* Show/Hide Answer Button */}
                {!alwaysShowAnswer && (
                  <div className="flex justify-center mb-6">
                    <button
                      onClick={() => setShowAnswer(!showAnswer)}
                      className={`px-8 py-3 rounded-2xl font-bold shadow-md hover:shadow-lg transition-all text-sm flex items-center justify-center gap-2 cursor-pointer ${showAnswer
                        ? 'bg-gray-255 dark:bg-slate-700 text-gray-800 dark:text-gray-200'
                        : `${theme.primary} text-white`
                        }`}
                    >
                      {showAnswer ? 'Hide Answer Explanation' : 'Reveal Answer Explanation'}
                    </button>
                  </div>
                )}

                {/* Answer Panel */}
                {(showAnswer || alwaysShowAnswer) && (
                  <div className={`mt-6 pt-6 border-t border-dashed border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/10 p-6 rounded-2xl`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-8 h-8 rounded-full ${theme.primary} text-white flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5`}>
                        A
                      </div>
                      <div className="flex-1 overflow-x-auto">
                        <h4 className={`text-md font-bold ${theme.primaryText} mb-3`}>Answer & Code Analysis:</h4>
                        <div className={`text-sm md:text-md leading-relaxed ${theme.text} whitespace-pre-line`}>
                          {renderText(currentQ.answer)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Question Pagination Control Footer */}
              <div className={`${theme.card} p-5 rounded-2xl shadow-md border border-gray-200/50 dark:border-slate-800 flex items-center justify-between`}>
                <button
                  onClick={() => {
                    if (currentQuestionIndex > 0) {
                      const prevQ = filteredQuestions[currentQuestionIndex - 1];
                      selectQuestionById(prevQ.id);
                    }
                  }}
                  disabled={currentQuestionIndex === 0}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all cursor-pointer ${currentQuestionIndex === 0
                    ? 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-600 cursor-not-allowed border border-transparent'
                    : 'bg-gray-250 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-200 text-gray-750 border border-gray-300/30'
                    }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="text-center hidden sm:block">
                  <span className="text-xs text-gray-500 font-bold block mb-1">GUIDE PROGRESS</span>
                  <span className={`text-sm font-bold ${theme.text}`}>{currentQuestionIndex + 1} / {filteredQuestions.length} Items</span>
                </div>

                <button
                  onClick={() => {
                    if (currentQuestionIndex < filteredQuestions.length - 1) {
                      const nextQ = filteredQuestions[currentQuestionIndex + 1];
                      selectQuestionById(nextQ.id);
                    }
                  }}
                  disabled={currentQuestionIndex === filteredQuestions.length - 1}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all cursor-pointer ${currentQuestionIndex === filteredQuestions.length - 1
                    ? 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-650 cursor-not-allowed border border-transparent'
                    : `${theme.primary} text-white hover:shadow`
                    }`}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Progress bar container */}
              <div className={`${theme.card} rounded-2xl shadow-md p-4`}>
                <div className="flex items-center justify-between text-xs text-gray-500 font-bold mb-1">
                  <span>Study session progress</span>
                  <span>{Math.round(((currentQuestionIndex + 1) / filteredQuestions.length) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-700 h-2 rounded-full">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentQuestionIndex + 1) / filteredQuestions.length) * 100}%` }}
                  />
                </div>
                <div className="mt-2 text-[10px] text-gray-405 font-bold text-center uppercase tracking-widest">
                  Tip: Use Left/Right Arrow keys to navigate, Spacebar to toggle answer.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Course Quiz Viewer Component (Routes: /quizzes/:courseId, /quizzes/:courseId/test, /quizzes/:courseId/practice, /quizzes/:courseId/results)
const CourseViewer = ({ theme, isMusicPlaying, setIsMusicPlaying, currentTheme, setCurrentTheme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId } = useParams();

  const currentCourse = useMemo(() => {
    return courses.find(c => c.id === courseId) || null;
  }, [courseId]);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [testStarted, setTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [testQuestions, setTestQuestions] = useState([]);
  const [showTopicSelector, setShowTopicSelector] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [isAutoRead, setIsAutoRead] = useState(false);

  const mode = useMemo(() => {
    if (location.pathname.endsWith('/test')) return 'test';
    if (location.pathname.endsWith('/results')) return 'results';
    if (location.pathname.endsWith('/practice')) return 'practice';
    return 'home';
  }, [location.pathname]);

  const questionBank = useMemo(() => {
    return currentCourse ? currentCourse.questions : [];
  }, [currentCourse]);

  const categories = useMemo(() => {
    return currentCourse
      ? ['all', ...new Set(questionBank.map(q => q.category))]
      : [];
  }, [currentCourse, questionBank]);

  // Timer Effect
  useEffect(() => {
    let timer;
    if (testStarted && timeLeft > 0 && mode === 'test') {
      timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    } else if (timeLeft === 0 && mode === 'test') {
      setTestStarted(false);
      navigate(`/quizzes/${courseId}/results`);
    }
    return () => clearInterval(timer);
  }, [testStarted, timeLeft, mode, navigate, courseId]);

  const handleAnswerSelect = (index) => {
    if (showAnswer) return;
    setSelectedAnswer(index);
  };

  const submitAnswer = () => {
    if (selectedAnswer !== null) {
      const questions = testQuestions.length ? testQuestions : questionBank;
      const isCorrect = selectedAnswer === questions[currentQuestion]?.correct;
      if (isCorrect) setScore(prev => prev + 1);
      setShowAnswer(true);
    }
  };

  const nextQuestion = () => {
    setSelectedAnswer(null);
    setShowAnswer(false);
    const questions = testQuestions.length ? testQuestions : questionBank;
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (mode === 'test') {
      setTestStarted(false);
      navigate(`/quizzes/${courseId}/results`);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0 && mode !== 'test') {
      setCurrentQuestion(prev => prev - 1);
      setSelectedAnswer(null);
      setShowAnswer(false);
    }
  };

  const startWithTopics = (topicsOverride) => {
    const topicsToUse = Array.isArray(topicsOverride) ? topicsOverride : selectedTopics;
    if (topicsToUse.length === 0) {
      alert('Please select at least one topic!');
      return;
    }

    let availableQuestions = questionBank.filter(q => topicsToUse.includes(q.category));
    availableQuestions = availableQuestions.sort(() => 0.5 - Math.random()).slice(0, Math.min(10, availableQuestions.length));

    const processed = processQuestions(availableQuestions);
    setTestQuestions(processed);
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowAnswer(false);
    setTimeLeft(600);
    setTestStarted(true);
    setShowTopicSelector(false);
    navigate(`/quizzes/${courseId}/test`);
  };

  const toggleTopic = (cat) => {
    if (selectedTopics.includes(cat)) {
      setSelectedTopics(selectedTopics.filter(t => t !== cat));
    } else {
      setSelectedTopics([...selectedTopics, cat]);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentCourse) {
    return <Navigate to="/quizzes" replace />;
  }

  const shareUrl = window.location.origin + `/quizzes/${currentCourse.id}`;

  if (mode === 'home') {
    return (
      <div className={`min-h-screen ${theme.bg} transition-colors duration-500`}>
        <SEOUpdater
          title={`${currentCourse.title} Practice Quizzes & Mock Tests | S4simple`}
          description={currentCourse.description}
        />
        <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
          <button
            onClick={() => navigate('/quizzes')}
            className={`flex items-center px-4 py-2 rounded-full ${theme.card} ${theme.text} shadow-md hover:shadow-lg transition-all font-semibold cursor-pointer`}
          >
            ← Back to Quizzes
          </button>
          <div className="hidden md:block">
            <S4SimpleLogo size={28} showText={true} textClassName="text-sm" />
          </div>
        </div>

        <HeaderControls
          isMusicPlaying={isMusicPlaying}
          setIsMusicPlaying={setIsMusicPlaying}
          currentTheme={currentTheme}
          setCurrentTheme={setCurrentTheme}
          theme={theme}
        />

        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center mb-12 pt-16">
            <div className="flex items-center justify-center mb-4 gap-3">
              {(() => {
                const Icon = { Code, Layout, Database, Award }[currentCourse?.icon] || Code;
                return <Icon className={`w-12 h-12 ${currentCourse?.accent || ''}`} />;
              })()}
              <h1 className={`text-5xl font-bold bg-gradient-to-r ${currentCourse?.color || ''} bg-clip-text text-transparent`}>
                {currentCourse?.title}
              </h1>
            </div>
            <p className={`${theme.subText} text-lg max-w-xl mx-auto`}>{currentCourse?.description}</p>
            <div className="mt-3 flex items-center justify-center gap-3">
              <span className={`${theme.primaryText} font-semibold`}>{questionBank.length} Questions Available</span>
              <ShareButton url={shareUrl} label="Share Quiz" />
            </div>
          </div>

          <div className="grid md:grid-cols-1 gap-6 mb-8 max-w-2xl mx-auto">
            <div className={`${theme.card} rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all border-2 border-transparent hover:${theme.border}`}>
              <Trophy className={`w-16 h-16 ${theme.accent} mb-4 mx-auto`} />
              <h2 className={`text-3xl font-bold ${theme.text} mb-3 text-center`}>Mock Interview Test</h2>
              <p className={`${theme.subText} text-center mb-6`}>
                Test your knowledge with 10 random questions in 10 minutes
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => {
                    const shuffled = [...questionBank].sort(() => 0.5 - Math.random()).slice(0, 10);
                    setTestQuestions(processQuestions(shuffled));
                    setCurrentQuestion(0);
                    setScore(0);
                    setSelectedAnswer(null);
                    setShowAnswer(false);
                    setTimeLeft(600);
                    setTestStarted(true);
                    setSelectedTopics([]);
                    navigate(`/quizzes/${courseId}/test`);
                  }}
                  className={`w-full ${theme.primary} text-white py-4 px-8 rounded-lg font-bold hover:shadow-lg transition-all text-xl cursor-pointer`}
                >
                  Start Mini Mock Test (10 Qs) →
                </button>

                <button
                  onClick={() => {
                    const shuffled = [...questionBank].sort(() => 0.5 - Math.random());
                    setTestQuestions(processQuestions(shuffled));
                    setCurrentQuestion(0);
                    setScore(0);
                    setSelectedAnswer(null);
                    setShowAnswer(false);
                    setTimeLeft(shuffled.length * 60);
                    setTestStarted(true);
                    setSelectedTopics([]);
                    navigate(`/quizzes/${courseId}/test`);
                  }}
                  className={`w-full bg-white border-2 ${theme.border} ${theme.primaryText} py-3 px-8 rounded-lg font-bold hover:bg-gray-50 transition-all text-lg cursor-pointer`}
                >
                  Start Full Course Test ({questionBank.length} Qs)
                </button>
              </div>
            </div>
          </div>

          <div className={`${theme.card} rounded-2xl shadow-lg p-8`}>
            <h3 className={`text-xl font-bold ${theme.text} mb-4`}>Topics Covered</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {categories.slice(1).map((cat, idx) => {
                const count = questionBank.filter(q => q.category === cat).length;
                return (
                  <div
                    key={idx}
                    onClick={() => startWithTopics([cat])}
                    role="button"
                    tabIndex={0}
                    className={`flex items-center justify-between ${theme.highlight} rounded-lg p-3 cursor-pointer hover:shadow-md transition-all`}
                  >
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <span className={`${theme.text} font-medium`}>{cat}</span>
                    </div>
                    <span className={`text-sm font-bold ${theme.primaryText}`}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showTopicSelector) {
    return (
      <div className={`min-h-screen ${theme.bg} transition-colors duration-500 flex items-center justify-center p-6`}>
        <div className={`${theme.card} rounded-2xl shadow-2xl p-8 max-w-3xl w-full`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-3xl font-bold ${theme.text}`}>Select Topics for Test</h2>
            <button
              onClick={() => setShowTopicSelector(false)}
              className={`${theme.subText} hover:${theme.text} text-2xl cursor-pointer`}
            >
              ×
            </button>
          </div>

          <p className={`${theme.subText} mb-6`}>
            Choose one or more topics you want to focus on. Up to 10 random questions will be selected from your chosen topics.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {categories.slice(1).map((cat) => {
              const count = questionBank.filter(q => q.category === cat).length;
              const isSelected = selectedTopics.includes(cat);
              const cardClass = isSelected
                ? `${theme.highlight} ${theme.border} shadow-md`
                : `${theme.card} ${theme.border} hover:shadow`;

              const checkClass = isSelected
                ? `${theme.primary} border-transparent`
                : 'border-gray-300';

              return (
                <div
                  key={cat}
                  onClick={() => toggleTopic(cat)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${cardClass}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-6 h-6 rounded border-2 mr-3 flex items-center justify-center ${checkClass}`}>
                        {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <span className={`font-semibold ${theme.text}`}>{cat}</span>
                    </div>
                    <span className={`text-sm font-bold ${theme.primaryText}`}>{count} Q's</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowTopicSelector(false)}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={startWithTopics}
              disabled={selectedTopics.length === 0}
              className={`flex-1 py-3 rounded-lg font-semibold transition-all ${selectedTopics.length > 0
                ? `${theme.primary} text-white hover:shadow-lg cursor-pointer`
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                } `}
            >
              Start Test ({selectedTopics.length} topic{selectedTopics.length !== 1 ? 's' : ''})
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'results') {
    const questionsList = testQuestions.length ? testQuestions : questionBank;
    const percentage = questionsList.length ? Math.round((score / questionsList.length) * 100) : 0;

    return (
      <div className={`min-h-screen ${theme.bg} transition-colors duration-500 flex items-center justify-center p-6`}>
        <div className={`${theme.card} rounded-2xl shadow-2xl p-12 max-w-2xl w-full text-center`}>
          <Trophy className={`w-20 h-20 mx-auto mb-6 ${percentage >= 70 ? 'text-yellow-500' : 'text-gray-400'} `} />
          <h2 className={`text-4xl font-bold ${theme.text} mb-4`}>Test Complete!</h2>
          <div className={`text-6xl font-bold ${theme.primary} bg-clip-text text-transparent mb-6`}>
            {score}/{questionsList.length}
          </div>
          <p className={`text-2xl ${theme.subText} mb-8`}>
            {percentage}% Correct
          </p>
          <div className="mb-8">
            {percentage >= 80 && <p className="text-green-600 text-xl font-semibold">Excellent! You're ready! 🎉</p>}
            {percentage >= 60 && percentage < 80 && <p className="text-blue-600 text-xl font-semibold">Good job! Keep practicing. 👍</p>}
            {percentage < 60 && <p className="text-orange-600 text-xl font-semibold">Keep studying! You'll get there. 💪</p>}
          </div>
          <button
            onClick={() => navigate(`/quizzes/${courseId}`)}
            className={`${theme.primary} text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer`}
          >
            Back to Course Overview
          </button>
        </div>
      </div>
    );
  }

  const questions = testQuestions.length ? testQuestions : questionBank;
  const currentQ = questions[currentQuestion];

  if (!currentQ) {
    return <Navigate to={`/quizzes/${courseId}`} replace />;
  }

  return (
    <div className={`min-h-screen ${theme.bg} transition-colors duration-500`}>
      <SEOUpdater
        title={`${currentCourse.title} - Question ${currentQuestion + 1} | S4simple`}
        description={currentCourse.description}
      />
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className={`${theme.card} rounded-2xl shadow-lg p-6 mb-6`}>
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/quizzes/${courseId}`)}
              className={`flex items-center ${theme.subText} hover:${theme.text} font-semibold cursor-pointer`}
            >
              ← Back to Course Overview
            </button>
            {mode === 'test' && testStarted && (
              <div className={`flex items-center ${theme.highlight} px-4 py-2 rounded-lg`}>
                <Clock className={`w-5 h-5 ${theme.primaryText} mr-2`} />
                <span className={`font-mono text-xl font-bold ${timeLeft < 60 ? 'text-red-600' : theme.primaryText}`}>
                  {formatTime(timeLeft)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Question Card */}
        <div className={`${theme.card} rounded-2xl shadow-xl p-8 mb-6`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className={`text-sm font-semibold ${theme.subText}`}>
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getDifficultyColor(currentQ.difficulty)}`}>
                {currentQ.difficulty}
              </span>
            </div>
            {mode === 'test' && (
              <div className={`text-sm font-semibold ${theme.subText}`}>
                Score: {score}/{currentQuestion + (showAnswer ? 1 : 0)}
              </div>
            )}
          </div>

          <div className={`mb-2 text-sm font-semibold ${theme.accent}`}>
            {currentQ.category}
          </div>

          <div className="flex justify-between items-start mb-6">
            <h3 className={`text-2xl font-bold ${theme.text} flex-1 mr-4`}>
              {renderText(currentQ.question)}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => speakText(currentQ.question)}
                className={`p-2 rounded-full ${theme.highlight} hover:bg-gray-200 transition-colors cursor-pointer`}
                title="Read Question"
              >
                <Headphones className={`w-5 h-5 ${theme.primaryText}`} />
              </button>
              <button
                onClick={() => setIsAutoRead(!isAutoRead)}
                className={`p-2 rounded-full ${isAutoRead ? theme.primary : theme.highlight} transition-colors cursor-pointer`}
                title={isAutoRead ? "Auto-Read On" : "Auto-Read Off"}
              >
                <Mic className={`w-5 h-5 ${isAutoRead ? 'text-white' : theme.subText}`} />
              </button>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {currentQ.options.map((option, idx) => {
              const isSelected = selectedAnswer === idx;
              const isCorrect = currentQ.correct === idx;
              const showResult = showAnswer;

              let bgColor = `bg-transparent hover:${theme.highlight} ${theme.border}`;
              let indicatorBg = 'bg-transparent';
              let indicatorBorder = 'border-current';
              let indicatorText = theme.text;
              let optionTextColor = theme.text;

              if (showResult) {
                if (isCorrect) {
                  bgColor = 'bg-green-50 border-green-500';
                  indicatorBg = 'bg-green-100';
                  indicatorBorder = 'border-green-600';
                  indicatorText = 'text-green-700';
                  optionTextColor = 'text-green-900';
                } else if (isSelected && !isCorrect) {
                  bgColor = 'bg-red-50 border-red-500';
                  indicatorBg = 'bg-red-100';
                  indicatorBorder = 'border-red-600';
                  indicatorText = 'text-red-700';
                  optionTextColor = 'text-red-900';
                }
              } else if (isSelected) {
                bgColor = `${theme.highlight} ${theme.primaryText.replace('text-', 'border-')}`;
                indicatorBg = theme.primary;
                indicatorBorder = 'border-transparent';
                indicatorText = 'text-white';
              } else {
                bgColor = `${theme.card} hover:${theme.highlight} ${theme.border}`;
                indicatorBg = theme.highlight;
              }

              return (
                <div
                  key={idx}
                  onClick={() => handleAnswerSelect(idx)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${bgColor} ${!showAnswer ? 'hover:shadow-md' : ''}`}
                >
                  <div className="flex items-center">
                    <span className={`w-8 h-8 rounded-full ${indicatorBg} border-2 ${indicatorBorder} flex items-center justify-center mr-3 font-bold ${indicatorText} transition-colors`}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className={`font-medium ${optionTextColor} flex-1`}>{renderText(option)}</span>
                    {showResult && isCorrect && (
                      <CheckCircle className="w-6 h-6 text-green-600 ml-auto" />
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <XCircle className="w-6 h-6 text-red-600 ml-auto" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {showAnswer && (
            <div className={`${theme.highlight} border-2 ${theme.border} rounded-lg p-4 mb-6`}>
              <div className="flex items-start">
                <div className={`w-6 h-6 rounded-full ${theme.primary} text-white flex items-center justify-center mr-3 mt-1`}>
                  ℹ
                </div>
                <div>
                  <h4 className={`font-semibold ${theme.primaryText} mb-2`}>Explanation:</h4>
                  <p className={theme.subText}>{renderText(currentQ.explanation)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {!showAnswer ? (
              <button
                onClick={submitAnswer}
                disabled={selectedAnswer === null}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all ${selectedAnswer !== null
                  ? `${theme.primary} text-white hover:shadow-lg cursor-pointer`
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  } `}
              >
                Submit Answer
              </button>
            ) : (
              <>
                {mode === 'practice' && currentQuestion > 0 && (
                  <button
                    onClick={prevQuestion}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all cursor-pointer"
                  >
                    ← Previous
                  </button>
                )}
                <button
                  onClick={nextQuestion}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer"
                >
                  {currentQuestion < questions.length - 1 ? 'Next Question →' : mode === 'test' ? 'Finish Test' : 'Restart'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}% ` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Root Main Application Component
const InterviewPrepApp = () => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      return localStorage.getItem('interviewPrep_theme') || 'light';
    } catch (e) {
      return 'light';
    }
  });

  const [masteredList, setMasteredList] = useState(() => {
    try {
      const saved = localStorage.getItem('interviewPrep_mastered');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const theme = themes[currentTheme];

  useEffect(() => {
    localStorage.setItem('interviewPrep_theme', currentTheme);
  }, [currentTheme]);

  const toggleMastered = (guideId, qId) => {
    const key = `${guideId}_${qId}`;
    const updated = { ...masteredList, [key]: !masteredList[key] };
    setMasteredList(updated);
    try {
      localStorage.setItem('interviewPrep_mastered', JSON.stringify(updated));
    } catch (e) { }
  };

  // Tibetan Monk/Symphonic Drone Effect Audio Synthesis
  useEffect(() => {
    let audioContext;
    let osc1, osc2, osc3;
    let gainNode;
    let filterNode;
    let lfo;
    let resumeAudio;

    const initAudio = () => {
      if (isMusicPlaying) {
        try {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          audioContext = new AudioContext();

          if (audioContext.state === 'suspended') {
            resumeAudio = () => {
              if (audioContext && audioContext.state !== 'closed') {
                audioContext.resume();
              }
              document.removeEventListener('click', resumeAudio);
              document.removeEventListener('keydown', resumeAudio);
            };
            document.addEventListener('click', resumeAudio);
            document.addEventListener('keydown', resumeAudio);
          }

          gainNode = audioContext.createGain();
          gainNode.gain.setValueAtTime(0, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 5);

          filterNode = audioContext.createBiquadFilter();
          filterNode.type = 'lowpass';
          filterNode.frequency.setValueAtTime(200, audioContext.currentTime);
          filterNode.Q.value = 1;

          lfo = audioContext.createOscillator();
          lfo.type = 'sine';
          lfo.frequency.setValueAtTime(0.1, audioContext.currentTime);
          const lfoGain = audioContext.createGain();
          lfoGain.gain.setValueAtTime(100, audioContext.currentTime);

          lfo.connect(lfoGain);
          lfoGain.connect(filterNode.frequency);
          lfo.start();

          osc1 = audioContext.createOscillator();
          osc1.type = 'sawtooth';
          osc1.frequency.setValueAtTime(55.00, audioContext.currentTime);

          osc2 = audioContext.createOscillator();
          osc2.type = 'sawtooth';
          osc2.frequency.setValueAtTime(55.25, audioContext.currentTime);

          osc3 = audioContext.createOscillator();
          osc3.type = 'triangle';
          osc3.frequency.setValueAtTime(82.41, audioContext.currentTime);

          osc1.connect(filterNode);
          osc2.connect(filterNode);
          osc3.connect(filterNode);

          filterNode.connect(gainNode);
          gainNode.connect(audioContext.destination);

          osc1.start();
          osc2.start();
          osc3.start();
        } catch (e) {
          console.error("Audio Context Error:", e);
        }
      }
    };

    initAudio();

    return () => {
      if (resumeAudio) {
        document.removeEventListener('click', resumeAudio);
        document.removeEventListener('keydown', resumeAudio);
      }
      try {
        if (gainNode) {
          gainNode.gain.cancelScheduledValues(audioContext.currentTime);
          gainNode.gain.setValueAtTime(gainNode.gain.value, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 1);
        }
        setTimeout(() => {
          if (osc1) osc1.stop();
          if (osc2) osc2.stop();
          if (osc3) osc3.stop();
          if (lfo) lfo.stop();
          if (audioContext && audioContext.state !== 'closed') audioContext.close();
        }, 1000);
      } catch (e) { }
    };
  }, [isMusicPlaying]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <DashboardView
            theme={theme}
            isMusicPlaying={isMusicPlaying}
            setIsMusicPlaying={setIsMusicPlaying}
            currentTheme={currentTheme}
            setCurrentTheme={setCurrentTheme}
            masteredList={masteredList}
          />
        }
      />
      <Route
        path="/quizzes"
        element={
          <DashboardView
            theme={theme}
            isMusicPlaying={isMusicPlaying}
            setIsMusicPlaying={setIsMusicPlaying}
            currentTheme={currentTheme}
            setCurrentTheme={setCurrentTheme}
            masteredList={masteredList}
          />
        }
      />
      <Route
        path="/guides/:guideId/:questionId?"
        element={
          <GuideViewer
            theme={theme}
            isMusicPlaying={isMusicPlaying}
            setIsMusicPlaying={setIsMusicPlaying}
            currentTheme={currentTheme}
            setCurrentTheme={setCurrentTheme}
            masteredList={masteredList}
            toggleMastered={toggleMastered}
          />
        }
      />
      <Route
        path="/quizzes/:courseId/*"
        element={
          <CourseViewer
            theme={theme}
            isMusicPlaying={isMusicPlaying}
            setIsMusicPlaying={setIsMusicPlaying}
            currentTheme={currentTheme}
            setCurrentTheme={setCurrentTheme}
          />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default InterviewPrepApp;